import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/common/SEO';

const analyticsUseCases = [
  { title: 'Demand Forecasting', desc: 'Predict future product demand with high precision, reducing inventory carrying costs and stockouts.' },
  { title: 'Churn Prediction', desc: 'Identify at-risk customers before they leave, enabling proactive retention strategies and improving LTV.' },
  { title: 'Fraud Detection', desc: 'Detect anomalous patterns in financial transactions in real-time, reducing losses and improving security.' },
  { title: 'Operational Optimisation', desc: 'Predict equipment failures or supply chain bottlenecks before they occur, ensuring business continuity.' },
  { title: 'Predictive Maintenance', desc: 'Analyse sensor data to predict when machinery requires service, avoiding costly unplanned downtime.' },
];

const faqs = [
  {
    q: 'How is predictive analytics different from traditional reporting?',
    a: 'Traditional reporting looks backward at what happened. Predictive analytics uses historical data and ML models to forecast what is likely to happen in the future.',
  },
  {
    q: 'What data is required for predictive AI agents?',
    a: 'Predictive agents require historical data from your ERP, CRM, or IoT systems. Eagentix agents can connect to 100+ enterprise data sources to build accurate models.',
  },
  {
    q: 'Can predictive models be deployed privately?',
    a: 'Yes. Eagentix supports private deployment on your own cloud or on-premises, ensuring your proprietary training data never leaves your environment.',
  },
];

export default function PredictiveAnalyticsAI() {
  const sectionLight = { padding: '80px 0', background: '#f8fafc' };
  const sectionWhite = { padding: '80px 0', background: 'white' };
  const sectionDark = { padding: '80px 0', background: '#0f172a' };
  const container = { maxWidth: '1100px', margin: '0 auto', padding: '0 24px' };
  const card = { borderRadius: '20px', border: '1px solid rgba(148,163,184,0.16)', background: 'white', padding: '28px 32px' };

  return (
    <>
      <SEO
        title="Predictive Analytics & ML AI Agents | Eagentix"
        description="Leverage predictive analytics and machine learning with enterprise AI agents. From demand forecasting to churn prediction, Eagentix delivers actionable future insights."
        pathname="/use-cases/predictive-analytics-ai"
      />

      {/* Hero */}
      <section style={{ ...sectionDark, paddingTop: '120px' }}>
        <div style={container}>
          <div style={{ maxWidth: '800px' }}>
            <span style={{ color: '#60a5fa', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
              Use Case · Predictive Analytics
            </span>
            <h1 style={{ fontSize: '3rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', margin: '16px 0 24px' }}>
              Predictive Analytics & Machine Learning Agents
            </h1>
            <p style={{ fontSize: '1.2rem', color: '#cbd5e1', lineHeight: 1.75, marginBottom: '36px' }}>
              Stop reacting and start anticipating. Eagentix provides enterprise AI agents that leverage your historical data to forecast trends, identify risks, and optimise operations with machine learning.
            </p>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <a
                href="https://app.eagentix.com/register"
                target="_blank"
                rel="noopener noreferrer"
                style={{ background: '#0071e3', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem' }}
              >
                Get Started
              </a>
              <a
                href="/#contact"
                style={{ background: 'rgba(255,255,255,0.1)', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem', border: '1px solid rgba(255,255,255,0.2)' }}
              >
                Talk to Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Predictive Solutions */}
      <section style={sectionLight}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '12px' }}>
            Future-Proof Your Decisions
          </h2>
          <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.75, marginBottom: '40px', maxWidth: '680px' }}>
            High-impact use cases for predictive AI agents across your enterprise.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
            {analyticsUseCases.map((item, i) => (
              <div key={i} style={card}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0071e3', marginBottom: '10px' }}>{item.title}</h3>
                <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.65, margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section style={sectionWhite}>
        <div style={container}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '64px', alignItems: 'center' }}>
            <div>
              <h2 style={{ fontSize: '2.5rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '24px' }}>
                Measurable ROI from Predictive ML
              </h2>
              <p style={{ color: '#475569', fontSize: '1.1rem', lineHeight: 1.8, marginBottom: '24px' }}>
                We don't just build models; we build agents that act on insights. From identifying forecasting errors to suggesting optimal inventory levels, our agents bridge the gap between analytics and execution.
              </p>
            </div>
            <div style={{ background: '#f1f5f9', borderRadius: '24px', padding: '40px', border: '1px solid rgba(0,0,0,0.05)' }}>
              <h4 style={{ color: '#0071e3', textTransform: 'uppercase', fontSize: '0.8rem', letterSpacing: '0.1em', marginBottom: '16px', fontWeight: 700 }}>
                Platform Capabilities
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ background: 'white', padding: '12px 16px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 600 }}>Automated Feature Engineering</div>
                <div style={{ background: 'white', padding: '12px 16px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 600 }}>Multi-Model Ensembling</div>
                <div style={{ background: 'white', padding: '12px 16px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 600 }}>Explainable AI (XAI) for Audit</div>
                <div style={{ background: 'white', padding: '12px 16px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 600 }}>Real-time Inference Pipelines</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={sectionLight}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '40px' }}>
            Predictive Analytics FAQ
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {faqs.map((faq, i) => (
              <div key={i} style={card}>
                <h3 style={{ color: '#0071e3', fontSize: '1.05rem', fontWeight: 700, margin: '0 0 12px 0' }}>{faq.q}</h3>
                <p style={{ color: '#475569', fontSize: '1rem', lineHeight: 1.75, margin: 0 }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={sectionDark}>
        <div style={{ ...container, textAlign: 'center' }}>
          <h2 style={{ fontSize: '2.2rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', marginBottom: '16px' }}>
            Anticipate Your Next Move
          </h2>
          <p style={{ color: '#cbd5e1', fontSize: '1.1rem', lineHeight: 1.75, marginBottom: '36px', maxWidth: '600px', margin: '0 auto 36px' }}>
            Schedule a session with our data scientists to explore how predictive AI agents can transform your enterprise operations.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="/#contact"
              style={{ background: '#0071e3', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem' }}
            >
              Book a Strategy Session
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
